import React from "react";
import flagMap from "../../../assets/utils/flagMap";
import regionMap from "../../../assets/utils/regionMap";
import "./EsimCard.css";

const EsimCard = ({ esim, selected = false, onToggle }) => {
  const formatDataSize = (mbValue) => {
    if (!mbValue) return "0 MB";
    if (mbValue >= 1024) {
      return `${(mbValue / 1024).toFixed(1)} GB`;
    }
    return `${mbValue.toFixed(0)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getFlagSrc = () => {
    const countryCode = esim.country_id?.toLowerCase();
    const regionCode = esim.region_id?.toLowerCase();

    if (countryCode && flagMap[countryCode]) {
      return flagMap[countryCode];
    }

    if (regionCode && regionMap[regionCode]) {
      return regionMap[regionCode];
    }

    console.warn(`No flag found for country: ${esim.country_id}, region: ${esim.region_id}`); // debug
    return "/icons/flags/default.png";
  };

  const getStatusConfig = () => {
    if (esim.status?.toLowerCase() === "terminated") {
      return {
        label: "Terminated",
        bgColor: "rgba(239, 68, 68, 0.15)",
        textColor: "#ef4444",
      };
    }

    if (!esim.installed) {
      return {
        label: "Not Installed",
        bgColor: "rgba(156, 163, 175, 0.15)",
        textColor: "var(--color-text-light)",
      };
    }

    const status = esim.status?.toLowerCase() || "unknown";

    switch (status) {
      case "active":
        return {
          label: "Active",
          bgColor: "rgba(34, 197, 94, 0.15)",
          textColor: "#22c55e",
        };
      case "inactive":
        return {
          label: "Inactive",
          bgColor: "rgba(234, 179, 8, 0.15)",
          textColor: "#eab308",
        };
      case "archived":
        return {
          label: "Archived",
          bgColor: "rgba(107, 114, 128, 0.15)",
          textColor: "#6b7280",
        };
      default:
        return {
          label: status.charAt(0).toUpperCase() + status.slice(1),
          bgColor: "rgba(59, 130, 246, 0.15)",
          textColor: "#3b82f6",
        };
    }
  };

  const isRegion = !!esim.region_id && !esim.country_id;

  const { label: statusLabel, bgColor: statusBgColor, textColor: statusTextColor } = getStatusConfig();

  return (
    <div className={`esim-card ${selected ? "esim-card--selected" : ""}`} onClick={() => onToggle?.(esim)}>
      <div className="esim-card-header">
        <button
          type="button"
          className={`esim-radio ${selected ? "esim-radio--checked" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.(esim);
          }}
          aria-pressed={selected}
        >
          <span className="esim-radio-circle" />
        </button>

        <span
          className="status-badge"
          style={{
            backgroundColor: statusBgColor,
            color: statusTextColor,
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div className="esim-label-row">
        <span className="esim-for">eSIM for</span>
      </div>

      <div className="esim-location">
        <img
          src={getFlagSrc()}
          alt={`${esim.country_name || esim.region_name || "eSIM"}`}
          className={`flag-icon ${isRegion ? "region-icon" : ""}`}
        />
        <span className="location-name">{esim.country_name || esim.region_name || "Global"}</span>
      </div>

      <div className="progress-section">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width:
                esim.data_left && esim.data_limit
                  ? `${Math.max((esim.data_left / esim.data_limit) * 100, 5)}%`
                  : "100%",
              backgroundColor: "var(--color-primary)",
            }}
          />
        </div>
        <span className="data-usage">
          {formatDataSize(esim.data_left)} / {formatDataSize(esim.data_limit)}
        </span>
      </div>

      <div className="valid-date">Activate before {formatDate(esim.activate_before)}</div>
    </div>
  );
};

export default EsimCard;
